import { CohortInputRule, CohortMethod, RECENT_N_LIMITS, parseCohortYm, sameCohortRule } from './cohort-forecast';
import { BUNDLE_WINDOW_RANGE, CohortTuned, OWN_HISTORY_MONTHS_RANGE, forecastGuardedCohorts, GuardedForecastInput, resolveConstants, usesRetentionChange, usesSeasonRatio } from './cohort-guards';

export const AUTO_MIN_MONTHS = 18;
export const SEASON_MIN_MONTHS = 12;
export const TREND_MIN_MONTHS = 24;
export const FULL_YEAR_TEST_MONTHS = 24;
export const FULL_YEAR_WINDOW = 12;

export const AUTO_FALLBACK_RULE: CohortInputRule = { method: 'wavg', n: 3 };

const AUTO_METHODS: Array<{ method: CohortMethod; n: number; tuneN?: boolean }> = [
  { method: 'wavg', n: 3, tuneN: true },
  { method: 'chain', n: 3 },
  { method: 'avg', n: 3, tuneN: true },
  { method: 'prev', n: 1 }
];

const tunedCounts = (preferred: number): number[] => {
  const [min, max] = RECENT_N_LIMITS;
  const out: number[] = [];
  for (let n = min; n <= max; n++) out.push(n);
  return out.sort((a, b) => Math.abs(a - preferred) - Math.abs(b - preferred) || a - b);
};

export interface AutoCandidate {
  rule: CohortInputRule;
  accuracy: number | null;
}

export interface AutoPick {
  rule: CohortInputRule;
  accuracy: number | null;
  months: number;
  window: number;
  tested: boolean;
  candidates: AutoCandidate[];
}

export interface AutoChoice {
  rule: CohortInputRule;
  accuracy: number | null;
  at?: string;
  prev?: { rule: CohortInputRule; accuracy: number | null } | null;
}

export type AutoChoiceMap = Record<string, AutoChoice>;

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

export function actualMonthsOf(input: Pick<GuardedForecastInput, 'cohorts' | 'cut'>): number {
  let first = -1;
  input.cohorts.forEach(c => {
    if (/^pre/i.test(String(c.label))) return;
    const ratio = c.ratio || [];
    for (let t = 0; t <= input.cut && t < ratio.length; t++) {
      if (isNum(ratio[t])) { if (first < 0 || t < first) first = t; break; }
    }
  });
  return first < 0 ? 0 : input.cut - first + 1;
}

export function seasonAllowed(months: number): boolean {
  return months >= SEASON_MIN_MONTHS;
}

export function trendAllowed(months: number, method: CohortMethod): boolean {
  return months >= TREND_MIN_MONTHS && method !== 'chain';
}

export function autoCandidates(trainingMonths: number, withTrend = true): CohortInputRule[] {
  const out: CohortInputRule[] = [];
  AUTO_METHODS.forEach(({ method, n, tuneN }) => {
    const seasons = seasonAllowed(trainingMonths) ? [true, false] : [false];
    const trends = withTrend && trendAllowed(trainingMonths, method) ? [true, false] : [false];
    (tuneN ? tunedCounts(n) : [n]).forEach(count => seasons.forEach(season => trends.forEach(trend => {
      const rule: CohortInputRule = { method, n: count };
      if (season) rule.season = true;
      if (trend) rule.trend = true;
      out.push(rule);
    })));
  });
  return out;
}

export function heldOutError(input: GuardedForecastInput, testCut: number): number | null {
  const { months, cohorts, sizes, cut } = input;
  const res = forecastGuardedCohorts(Object.assign({}, input, { cut: testCut, override: undefined, explain: false }));
  const pre = cohorts.map(c => /^pre/i.test(String(c.label)));
  const birth = cohorts.map((c, j) => (pre[j] ? -1 : months.indexOf(parseCohortYm(c.label))));
  const errors: number[] = [];
  for (let t = testCut + 1; t <= cut && t < months.length; t++) {
    let p = 0, a = 0, bundle = 0, bundleSeen = false;
    cohorts.forEach((c, j) => {
      const size = sizes[j];
      const actual = (c.ratio || [])[t];
      if (!isNum(size) || size <= 0 || !isNum(actual)) return;
      if (res.legacy.folded[j]) {
        bundle += size * actual;
        bundleSeen = true;
        return;
      }
      if (!pre[j] && (birth[j] < 0 || t <= birth[j])) return;
      const predicted = res.rates[j] ? res.rates[j][t] : null;
      if (!isNum(predicted)) return;
      p += size * predicted;
      a += size * actual;
    });
    const legacy = res.legacy.on ? res.legacy.orders[t] : null;
    if (bundleSeen && isNum(legacy)) {
      p += legacy;
      a += bundle;
    }
    if (a > 0) errors.push(Math.abs(p - a) / a);
  }
  return errors.length ? errors.reduce((s, x) => s + x, 0) / errors.length : null;
}

function backTest(input: GuardedForecastInput, rule: CohortInputRule, testCut: number): number | null {
  const { months, cohorts, sizes, cut } = input;
  const run: GuardedForecastInput = {
    months,
    cut: testCut,
    cohorts,
    sizes,
    ruleFor: () => rule,
    channelRule: rule,
    scope: input.scope,
    constants: input.constants,
    engine: input.engine,
    explain: false
  };
  if (usesRetentionChange(input.engine)) {
    const error = heldOutError(Object.assign({}, run, { cut }), testCut);
    return isNum(error) ? Math.max(0, 1 - error) : null;
  }
  const res = forecastGuardedCohorts(run);
  const birth = cohorts.map(c => (/^pre/i.test(String(c.label)) ? -1 : months.indexOf(parseCohortYm(c.label))));
  let gap = 0;
  let total = 0;
  for (let t = testCut + 1; t <= cut; t++) {
    let p = 0;
    let a = 0;
    cohorts.forEach((c, j) => {
      if (birth[j] < 0 || t <= birth[j] || res.legacy.folded[j]) return;
      const size = sizes[j];
      const actual = (c.ratio || [])[t];
      const predicted = res.rates[j] ? res.rates[j][t] : null;
      if (!isNum(size) || size <= 0 || !isNum(actual) || !isNum(predicted)) return;
      p += size * predicted;
      a += size * actual;
    });
    gap += Math.abs(p - a);
    total += a;
  }
  return total > 0 ? Math.max(0, 1 - gap / total) : null;
}

export function backTestWindow(input: Pick<GuardedForecastInput, 'cohorts' | 'cut' | 'constants' | 'engine'>, months = actualMonthsOf(input)): number {
  return usesSeasonRatio(input.engine) && months >= FULL_YEAR_TEST_MONTHS ? FULL_YEAR_WINDOW : resolveConstants(input.constants).autoWindow;
}

export function pickAutoRule(input: GuardedForecastInput): AutoPick {
  const months = actualMonthsOf(input);
  const window = backTestWindow(input, months);
  const fallback: CohortInputRule = seasonAllowed(months) ? { ...AUTO_FALLBACK_RULE, season: true } : { ...AUTO_FALLBACK_RULE };
  if (months < AUTO_MIN_MONTHS || input.cut - window < 0) {
    return { rule: fallback, accuracy: null, months, window, tested: false, candidates: [] };
  }
  const testCut = input.cut - window;
  const candidates = autoCandidates(months - window, !usesRetentionChange(input.engine)).map(rule => ({ rule, accuracy: backTest(input, rule, testCut) }));
  let best: AutoCandidate | null = null;
  candidates.forEach(c => {
    if (isNum(c.accuracy) && (!best || (c.accuracy as number) > (best.accuracy as number) + 1e-12)) best = c;
  });
  const chosen = best as AutoCandidate | null;
  return chosen
    ? { rule: chosen.rule, accuracy: chosen.accuracy, months, window, tested: true, candidates }
    : { rule: fallback, accuracy: null, months, window, tested: false, candidates };
}

type TunedKey = 'bundleRetentionWindow' | 'ownHistoryMinMonths' | 'ownHistoryOn';

const rangeOf = ([lo, hi]: [number, number]) => {
  const out: number[] = [];
  for (let v = lo; v <= hi; v++) out.push(v);
  return out;
};

export function tuneCohortSettings(input: GuardedForecastInput): CohortTuned {
  const scope = input.scope || {};
  const user = scope.user || {};
  const prior = scope.tuned || {};
  const v4 = usesSeasonRatio(input.engine);
  const priorOf = (key: TunedKey) => (isNum(prior[key]) ? (prior[key] as number) : null);
  const held: Partial<Record<TunedKey, number | null>> = v4
    ? { ownHistoryMinMonths: priorOf('ownHistoryMinMonths') }
    : { bundleRetentionWindow: priorOf('bundleRetentionWindow'), ownHistoryMinMonths: priorOf('ownHistoryMinMonths'), ownHistoryOn: priorOf('ownHistoryOn') };
  const cut = input.months[input.cut];
  const months = actualMonthsOf(input);
  const window = backTestWindow(input, months);
  if (!usesRetentionChange(input.engine) || months < AUTO_MIN_MONTHS || input.cut - window < 0) {
    return Object.assign(Object.keys(held).reduce((acc, key) => Object.assign(acc, { [key]: null }), {}), { error: null, cut });
  }
  const testCut = input.cut - window;
  const errorWith = (tuned: Partial<Record<TunedKey, number | null>>) => heldOutError(Object.assign({}, input, { scope: Object.assign({}, scope, { tuned }) }), testCut);
  const search = (key: TunedKey, values: number[], extra?: Partial<Record<TunedKey, number>>) => {
    if (user[key]) return;
    const errors = values.map(v => errorWith(Object.assign({}, held, extra, { [key]: v })));
    let best = -1;
    errors.forEach((e, i) => { if (isNum(e) && (best < 0 || e < (errors[best] as number) - 1e-12)) best = i; });
    if (best < 0) return;
    const now = values.indexOf(held[key] as number);
    held[key] = now >= 0 && isNum(errors[now]) && Math.abs((errors[now] as number) - (errors[best] as number)) <= 1e-12 ? values[now] : values[best];
  };
  const finish = (extra?: Partial<CohortTuned>) => {
    const error = errorWith(held);
    return Object.assign({}, held, { error: isNum(error) ? Math.round(error * 1e6) / 1e6 : null, cut }, extra);
  };
  if (v4) {
    search('ownHistoryMinMonths', rangeOf(OWN_HISTORY_MONTHS_RANGE));
    return finish({ window });
  }
  search('bundleRetentionWindow', rangeOf(BUNDLE_WINDOW_RANGE));
  search('ownHistoryMinMonths', rangeOf(OWN_HISTORY_MONTHS_RANGE), { ownHistoryOn: 1 });
  if (!user.ownHistoryOn) {
    const on = errorWith(Object.assign({}, held, { ownHistoryOn: 1 }));
    const off = errorWith(Object.assign({}, held, { ownHistoryOn: 0 }));
    held.ownHistoryOn = isNum(on) && (!isNum(off) || on < off - 1e-12) ? 1 : 0;
  }
  return finish();
}

export function sanitizeAutoChoices(raw: unknown): AutoChoiceMap {
  const out: AutoChoiceMap = {};
  if (!raw || typeof raw !== 'object') return out;
  const ruleOf = (v: unknown): CohortInputRule | null => {
    if (!v || typeof v !== 'object') return null;
    const r = v as Record<string, unknown>;
    const m = AUTO_METHODS.find(x => x.method === r.method);
    const n = Number(r.n);
    if (!m || !Number.isInteger(n) || n < 1 || n > 36) return null;
    const rule: CohortInputRule = { method: m.method, n };
    if (r.season === true) rule.season = true;
    if (r.trend === true && m.method !== 'chain') rule.trend = true;
    return rule;
  };
  const accOf = (v: unknown) => (isNum(v) && v >= 0 && v <= 1 ? v : null);
  Object.keys(raw as Record<string, unknown>).forEach(key => {
    const parts = key.split('|');
    if (parts.length !== 2 || parts.some(p => !p)) return;
    const v = (raw as Record<string, unknown>)[key] as Record<string, unknown> | null;
    const rule = v ? ruleOf(v.rule) : null;
    if (!rule) return;
    const choice: AutoChoice = { rule, accuracy: accOf(v!.accuracy) };
    if (typeof v!.at === 'string' && v!.at) choice.at = v!.at;
    const prev = v!.prev as Record<string, unknown> | null | undefined;
    const prevRule = prev ? ruleOf(prev.rule) : null;
    if (prevRule) choice.prev = { rule: prevRule, accuracy: accOf(prev!.accuracy) };
    out[key] = choice;
  });
  return out;
}

export function sameAutoChoice(a: AutoChoice | null | undefined, b: AutoChoice | null | undefined): boolean {
  if (!a || !b) return !a && !b;
  return sameCohortRule(a.rule, b.rule) && (a.accuracy ?? null) === (b.accuracy ?? null);
}
